import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = ["canvas", "checkedSeries", "exportLink"]
  static values = { init: Object, format: String, selectedSequences: Array }

  connect() {
    this.selectedValues = this.#getInitialSelectedValues()
    "combo" in this.initValue ? this.renderCombo() : this.render()
  }

  disconnect() {
    this.chart?.destroy()
    this.chart = null
  }

  #getInitialSelectedValues() {
    const values = Array.isArray(this.selectedSequencesValue)
      ? this.selectedSequencesValue.map((val) => parseInt(val, 10))
      : []
    return values.length > 0 ? values : null
  }

  renderWithNames({ detail: { values } }) {
    this.selectedValues = values
    if (this.chart) {
      const { config, indices } = this.#buildConfig({ selectedValues: values })
      this.#applyUnits(indices)
      this.#ensureChart(config)
    }
  }

  async update({ detail: { params } }) {
    const url = `${location.href.split("?")[0]}?${new URLSearchParams(params)}`
    const response = await get(url, { responseKind: "json" })
    const { chart, stream } = await response.json
    if (chart) this.#updateChart(chart)
    if (stream) Turbo.renderStreamMessage(stream)

    if (this.hasExportLinkTarget) {
      const exportUrl = new URL(this.exportLinkTarget.href)
      Object.entries(params).forEach(([key, value]) => {
        exportUrl.searchParams.set(key, value)
      })
      this.exportLinkTarget.href = exportUrl.toString()
    }
  }

  render() {
    if (!this.hasInitValue) return

    const { config, indices } = this.#buildConfig({
      series: this.initValue.series,
      options: this.initValue.options,
      selectedValues: this.selectedValues,
    })

    this.#applyUnits(indices)
    this.#ensureChart(config)
  }

  renderCombo() {
    this.#renderOrUpdateCombo(this.selectedValues || this.#getCheckedNames())
  }

  #patchYaxisFormatter(yaxis) {
    if (!yaxis) return yaxis

    return Array.isArray(yaxis)
      ? yaxis.map((y) => ({
          ...y,
          labels: {
            ...(y.labels || {}),
            formatter: (v) => (typeof v === "number" ? v.toFixed(2) : v),
          },
        }))
      : {
          ...yaxis,
          labels: {
            ...(yaxis.labels || {}),
            formatter: (v) => (typeof v === "number" ? v.toFixed(2) : v),
          },
        }
  }

  #filterYaxis(yaxis, names) {
    if (!Array.isArray(yaxis)) return yaxis
    const hasOpposite = yaxis.some(
      (y) =>
        y.opposite &&
        ((Array.isArray(y.seriesName) &&
          y.seriesName.some((n) => names.includes(n))) ||
          (typeof y.seriesName === "string" && names.includes(y.seriesName))),
    )
    return hasOpposite ? yaxis : yaxis.filter((y) => !y.opposite)
  }

  #getCheckedNames() {
    return this.checkedSeriesTargets?.length
      ? this.checkedSeriesTargets
          .filter((el) => el.checked)
          .map((el) => el.dataset.seriesName)
      : this.initValue.series.map((s) => s.name)
  }

  #renderOrUpdateCombo(checkedNames) {
    const { indices, config } = this.#buildConfig({
      series: this.initValue.series,
      options: this.initValue.options,
      selectedValues: checkedNames,
    })
    this.#applyUnits(indices)
    this.#ensureChart(config)
  }

  #buildComboConfig(checkedItems) {
    const { series, options } = this.initValue
    if (!series) return { indices: [], config: options }

    const filtered = series
      .map((s, i) => ({ ...s, _metaIndex: i }))
      .filter((s) => {
        if (!s.name || !Array.isArray(checkedItems)) return false

        if (checkedItems.includes(s.name)) return true

        const nameIdMatch = s.name.match(/\d+/)
        if (!nameIdMatch) return false

        const nameId = parseInt(nameIdMatch[0])
        return checkedItems.some((item) => parseInt(item) === nameId)
      })

    const indices = filtered.map((s) => s._metaIndex)
    const yaxis = this.#filterYaxis(
      options?.yaxis,
      filtered.map((s) => s.name),
    )

    const config = {
      ...(options || {}),
      yaxis: this.#patchYaxisFormatter(yaxis),
      series: filtered.length > 0 ? filtered : [{ name: "无数据", data: [] }],
      tooltip: {
        ...(options?.tooltip || {}),
        custom: this.#tooltipFormatter.bind(this),
      },
    }

    if ("combo" in this.initValue && options?.fill) {
      config.fill = {
        ...options.fill,
        type: indices.map((i) => options.fill.type[i]),
        gradient: options.fill.gradient
          ? {
              ...options.fill.gradient,
              colorStops: indices.map(
                (i) => options.fill.gradient.colorStops[i],
              ),
            }
          : undefined,
      }
    }

    return { indices, config }
  }

  #buildConfig({
    series = this.initValue.series,
    options = this.initValue.options,
    selectedValues,
  } = {}) {
    const shouldFilter = selectedValues !== null && selectedValues !== undefined
    if (shouldFilter) {
      return this.#buildComboConfig(selectedValues)
    }

    const config = {
      ...(options || {}),
      series: series || [],
      yaxis: this.#patchYaxisFormatter(options?.yaxis),
      tooltip: {
        ...(options?.tooltip || {}),
        custom: this.#tooltipFormatter.bind(this),
      },
    }

    if (!config.series || config.series.length === 0) {
      config.series = [{ name: "无数据", data: [] }]
    }

    return { config }
  }

  #updateChart(chartData) {
    const { series, extra_options, units } = chartData
    this.initValue = {
      ...this.initValue,
      series,
      options: { ...this.initValue.options, ...extra_options },
      units,
    }

    const { config, indices } = this.#buildConfig({
      series: this.initValue.series,
      options: this.initValue.options,
      selectedValues: this.selectedValues,
    })

    this.#applyUnits(indices, this.selectedValues ? undefined : units)
    this.#ensureChart(config)
  }

  #ensureChart(config) {
    if (this.chart) {
      // redrawPaths=true 强制重新绘制路径，解决 series 数量变化时的渲染问题
      const seriesCount = config.series?.length || 0
      const prevSeriesCount = this.prevSeriesCount || 0
      const redrawPaths = seriesCount !== prevSeriesCount

      this.chart.resetSeries()

      this.chart.updateOptions(config, redrawPaths)
      this.prevSeriesCount = seriesCount
      return
    }

    this.prevSeriesCount = config.series?.length || 0
    this.chart = new ApexCharts(this.canvasTarget, config)
    this.chart.render()
  }

  #applyUnits(indices, fallback = this.initValue.units || []) {
    if (Array.isArray(indices)) {
      this.units = indices.map((i) => this.initValue.units?.[i] || "")
      return
    }
    this.units = Array.isArray(fallback) ? fallback : []
  }

  #getSeriesIndex(w, seriesIndex, dataPointIndex, hoveredTimestamp) {
    const seriesX = w?.globals?.seriesX?.[seriesIndex]
    if (!seriesX) return -1

    if (seriesX[dataPointIndex] === hoveredTimestamp) return dataPointIndex

    return seriesX.findIndex((t) => t === hoveredTimestamp)
  }

  #nonCandlePart(w, seriesIndex, idx) {
    const name = w.globals.seriesNames[seriesIndex]
    const unit = this.units?.[seriesIndex]

    const seriesType = w.config.series[seriesIndex]?.type || w.config.chart.type

    if (seriesType === "rangeArea") {
      const low = w?.globals?.seriesRangeStart?.[seriesIndex]?.[idx]
      const high = w?.globals?.seriesRangeEnd?.[seriesIndex]?.[idx]

      if (!low || !high) return ""

      const lowStr = Number(low).toFixed(2)
      const highStr = Number(high).toFixed(2)
      return this.#tooltipRow(name, `[${lowStr}, ${highStr}]`, unit)
    }

    const val = w?.globals?.series?.[seriesIndex]?.[idx]
    if (val === undefined || val === null) return ""

    return this.#tooltipRow(name, Number(val).toFixed(2), unit)
  }

  #candlePart(w, idx, seriesIndex) {
    if (!w?.globals?.seriesCandleO || !w.globals.seriesCandleO[0]) return ""
    const o = w.globals.seriesCandleO[0][idx]
    if (o === undefined || o === null) return ""
    const h = w.globals.seriesCandleH[0][idx]
    const l = w.globals.seriesCandleL[0][idx]
    const c = w.globals.seriesCandleC[0][idx]

    const unit = this.units?.[seriesIndex]

    return `
        <div class="flex flex-col gap-1 mb-2">
          ${this.#tooltipRow("开盘", o?.toFixed(2), unit)}
          ${this.#tooltipRow("最高", h?.toFixed(2), unit)}
          ${this.#tooltipRow("最低", l?.toFixed(2), unit)}
          ${this.#tooltipRow("收盘", c?.toFixed(2), unit)}
        </div>
      `
  }

  #tooltipRow(name, value, unit) {
    return `
      <div class="flex justify-between gap-4"><span class="text-gray-400">${name}</span><span class="text-white font-semibold">${value}${
        unit ? ` ${unit}` : ""
      }</span></div>
    `
  }
  #tooltipFormatter({ series, seriesIndex, dataPointIndex, w }) {
    const hoveredTimestamp = w.globals.seriesX[seriesIndex]?.[dataPointIndex]
    const label = hoveredTimestamp ? this.#formatLabel(hoveredTimestamp) : ""

    const parts = []
    w.config.series.forEach((s, i) => {
      if (!s || !w.globals.seriesNames || !w.globals.seriesNames[i]) return

      const type = s.type || w.config.chart.type

      const idx = this.#getSeriesIndex(w, i, dataPointIndex, hoveredTimestamp)
      if (idx === -1) return

      if (type === "candlestick") {
        const part = this.#candlePart(w, idx, i)
        if (part) parts.push(part)
      } else {
        const part = this.#nonCandlePart(w, i, idx)
        if (part) parts.push(part)
      }
    })

    if (parts.length === 0) return ""

    return `
      <div class="bg-[#071437] text-[13px] px-3 py-2 rounded-md shadow-lg min-w-40">
        <div class="text-white text-xs mb-1.5">${label}</div>
        <div class="flex flex-col gap-0.5">${parts.join("")}</div>
      </div>`
  }

  #formatLabel(timestamp) {
    if (typeof timestamp === "string") return timestamp
    const d = new Date(timestamp)
    const formats = {
      month: () =>
        d.toLocaleDateString("sv-SE", { year: "numeric", month: "2-digit" }),
      date: () => d.toLocaleDateString("sv-SE"),
      datetime: () =>
        d.toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }),
      time: () =>
        d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }),
    }
    return (formats[this.formatValue] ?? formats.time)()
  }
}
